import React, { useState, useMemo } from 'react';
import {
    Box,
    Paper,
    Typography,
    Grid,
    IconButton,
    Tooltip,
    useTheme,
    Button,
    Chip,
    List,
    ListItem,
    ListItemText,
    Divider,
    Avatar,
    ListItemAvatar,
    Fade,
} from '@mui/material';
import {
    ChevronLeft,
    ChevronRight,
    Event,
    Euro,
    ShowChart,
    Today,
    ReceiptLong,
} from '@mui/icons-material';
import {
    AreaChart,
    Area,
    XAxis,
    YAxis,
    Tooltip as RechartsTooltip,
    ResponsiveContainer,
} from 'recharts';
import {
    format,
    startOfMonth,
    endOfMonth,
    startOfWeek,
    endOfWeek,
    eachDayOfInterval,
    isSameMonth,
    isSameDay,
    addMonths,
    subMonths,
    parseISO,
} from 'date-fns';
import { ptBR, enUS } from 'date-fns/locale';
import { FormattedMessage, useIntl } from 'react-intl';
import { Expense } from '../types/Expense';
import { designTokens } from '../theme/designTokens';
import { getCategoryIcon } from '../utils/categoryIcons';
import { getBrandLogo } from '../utils/brandUtils';

interface CalendarViewProps {
    expenses: Expense[];
    categories: string[];
    payers: string[];
    payerColors: { [key: string]: string };
    categoryIcons?: { [key: string]: string };
    locale: 'pt-PT' | 'en-US';
}

const CalendarView: React.FC<CalendarViewProps> = ({
    expenses,
    categories,
    payers,
    payerColors,
    categoryIcons = {},
    locale,
}) => {
    const intl = useIntl();
    const theme = useTheme();
    const [currentDate, setCurrentDate] = useState(new Date());
    const [selectedDate, setSelectedDate] = useState<Date | null>(new Date());

    const dateFnsLocale = locale === 'pt-PT' ? ptBR : enUS;

    const monthStart = startOfMonth(currentDate);
    const monthEnd = endOfMonth(monthStart);
    const startDate = startOfWeek(monthStart, { locale: dateFnsLocale });
    const endDate = endOfWeek(monthEnd, { locale: dateFnsLocale });

    const calendarDays = eachDayOfInterval({
        start: startDate,
        end: endDate,
    });

    const weekDays = useMemo(() => {
        const days = [];
        let day = startDate;
        for (let i = 0; i < 7; i++) {
            days.push(format(day, 'EEEE', { locale: dateFnsLocale }));
            day = new Date(day.getTime() + 24 * 60 * 60 * 1000); // Add 1 day safely
        }
        return days;
    }, [startDate, dateFnsLocale]);

    const expensesByDate = useMemo(() => {
        const expensesMap: { [key: string]: Expense[] } = {};
        expenses.forEach((expense) => {
            const dateKey = format(parseISO(expense.date), 'yyyy-MM-dd');
            if (!expensesMap[dateKey]) {
                expensesMap[dateKey] = [];
            }
            expensesMap[dateKey].push(expense);
        });
        return expensesMap;
    }, [expenses]);

    const selectedDayExpenses = useMemo(() => {
        if (!selectedDate) return [];
        const dateKey = format(selectedDate, 'yyyy-MM-dd');
        return expensesByDate[dateKey] || [];
    }, [selectedDate, expensesByDate]);

    const chartData = useMemo(() => {
        return calendarDays
            .filter(day => isSameMonth(day, currentDate))
            .map(day => {
                const dateKey = format(day, 'yyyy-MM-dd');
                const dayTotal = (expensesByDate[dateKey] || []).reduce((sum, expense) => sum + expense.amount, 0);
                return {
                    day: format(day, 'd'),
                    amount: dayTotal,
                    fullDate: dateKey,
                };
            });
    }, [calendarDays, currentDate, expensesByDate]);

    const handlePrevMonth = () => setCurrentDate(subMonths(currentDate, 1));
    const handleNextMonth = () => setCurrentDate(addMonths(currentDate, 1));
    const handleDateClick = (date: Date) => setSelectedDate(date);

    const getDayTotal = (dayExpenses: Expense[]) => {
        return dayExpenses.reduce((sum, expense) => sum + expense.amount, 0);
    };

    const maxDailyExpense = useMemo(() => {
        let max = 0;
        calendarDays.forEach(day => {
            if (isSameMonth(day, currentDate)) {
                const dateKey = format(day, 'yyyy-MM-dd');
                const dayExpenses = expensesByDate[dateKey] || [];
                const total = dayExpenses.reduce((sum, expense) => sum + expense.amount, 0);
                if (total > max) max = total;
            }
        });
        return max > 0 ? max : 1; // Avoid division by zero
    }, [calendarDays, currentDate, expensesByDate]);

    return (
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3, height: '100%' }}>
            {/* Calendar Section */}
            <Box sx={{ flex: { xs: '1 1 auto', md: '2 1 0' }, display: 'flex', flexDirection: 'column' }}>
                <Paper
                    sx={{
                        p: 2,
                        mb: 2,
                        borderRadius: designTokens.borderRadius.lg,
                        background: designTokens.colors.background.paper,
                        border: designTokens.glassmorphism.border,
                        boxShadow: designTokens.shadows.md,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                    }}
                >
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <IconButton onClick={handlePrevMonth}>
                            <ChevronLeft />
                        </IconButton>
                        <Button 
                            onClick={() => setCurrentDate(new Date())}
                            size="small"
                            variant="outlined"
                            startIcon={<Today />}
                            sx={{ 
                                borderRadius: designTokens.borderRadius.full,
                                borderColor: designTokens.colors.background.elevated,
                                color: designTokens.colors.text.secondary,
                                textTransform: 'none',
                                display: { xs: 'none', sm: 'flex' }
                            }}
                        >
                            <FormattedMessage id="today" defaultMessage="Today" />
                        </Button>
                    </Box>
                    <Typography variant="h5" fontWeight="bold" sx={{ textTransform: 'capitalize' }}>
                        {format(currentDate, 'MMMM yyyy', { locale: dateFnsLocale })}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <IconButton onClick={handleNextMonth}>
                            <ChevronRight />
                        </IconButton>
                    </Box>
                </Paper>

                <Paper
                    sx={{
                        p: 1.5,
                        flexGrow: 1,
                        borderRadius: designTokens.borderRadius.lg,
                        background: designTokens.colors.background.paper,
                        border: designTokens.glassmorphism.border,
                        boxShadow: designTokens.shadows.md,
                        overflow: 'auto',
                    }}
                >
                    <Grid container columns={7} spacing={0.5} sx={{ height: '100%' }}>
                        {/* Weekday Headers */}
                        {weekDays.map((day) => (
                            <Grid item xs={1} key={day} sx={{ textAlign: 'center', py: 1 }}>
                                <Typography variant="subtitle2" fontWeight="bold" color="text.secondary" sx={{ textTransform: 'capitalize' }}>
                                    {day}
                                </Typography>
                            </Grid>
                        ))}

                        {/* Calendar Days */}
                        {calendarDays.map((day, index) => {
                            const dateKey = format(day, 'yyyy-MM-dd');
                            const dayExpenses = expensesByDate[dateKey] || [];
                            const totalAmount = getDayTotal(dayExpenses);
                            const isSelected = selectedDate && isSameDay(day, selectedDate);
                            const isCurrentMonth = isSameMonth(day, currentDate);
                            const isToday = isSameDay(day, new Date());

                            // Calculate heatmap intensity (0 to 1)
                            const intensity = totalAmount > 0 ? Math.min(1, totalAmount / maxDailyExpense) : 0;

                            return (
                                <Grid
                                    item
                                    xs={1}
                                    key={day.toISOString()}
                                    onClick={() => handleDateClick(day)}
                                >
                                    <Fade in={true} timeout={300 + (index * 15)}>
                                        <Box
                                            sx={{
                                                height: { xs: '60px', sm: '80px' },
                                                borderRadius: designTokens.borderRadius.md,
                                                p: 1,
                                                cursor: 'pointer',
                                                bgcolor: isSelected
                                                    ? `${designTokens.colors.primary.main}1A` // 10% opacity primary
                                                    : isToday
                                                        ? `${designTokens.colors.warning.main}1A`
                                                        : designTokens.colors.background.elevated,
                                                border: isSelected 
                                                    ? `1px solid ${designTokens.colors.primary.main}80` 
                                                    : `1px solid transparent`,
                                                transition: `all ${designTokens.transitions.fast}`,
                                                '&:hover': {
                                                    transform: 'translateY(-2px)',
                                                    boxShadow: designTokens.shadows.sm,
                                                    bgcolor: isSelected ? `${designTokens.colors.primary.main}33` : designTokens.colors.background.paper,
                                                },
                                                display: 'flex',
                                                flexDirection: 'column',
                                                justifyContent: 'space-between',
                                                opacity: isCurrentMonth ? 1 : 0.4,
                                                position: 'relative',
                                                overflow: 'hidden'
                                            }}
                                        >
                                            <Box display="flex" justifyContent="space-between">
                                                <Typography
                                                    variant="body2"
                                                    fontWeight={isToday ? 'bold' : 'normal'}
                                                    sx={{
                                                        bgcolor: isToday ? designTokens.colors.warning.main : 'transparent',
                                                        color: isToday ? '#fff' : designTokens.colors.text.primary,
                                                        borderRadius: '50%',
                                                        width: 24,
                                                        height: 24,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                    }}
                                                >
                                                    {format(day, 'd')}
                                                </Typography>
                                                {dayExpenses.length > 0 && (
                                                    <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: designTokens.colors.error.main }} />
                                                )}
                                            </Box>

                                            {dayExpenses.length > 0 && (
                                                <Box sx={{ zIndex: 1 }}>
                                                    <Typography variant="caption" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', color: designTokens.colors.text.primary, lineHeight: 1 }}>
                                                        <Euro sx={{ fontSize: 10, mr: 0.2 }} /> {totalAmount.toFixed(0)}
                                                    </Typography>
                                                    <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap', height: 6, overflow: 'hidden' }}>
                                                        {dayExpenses.slice(0, 3).map((exp, i) => (
                                                            <Box
                                                                key={i}
                                                                sx={{
                                                                    width: 6,
                                                                    height: 6,
                                                                    borderRadius: '50%',
                                                                    bgcolor: payerColors[exp.payer] || designTokens.colors.text.secondary,
                                                                }}
                                                            />
                                                        ))}
                                                    </Box>
                                                </Box>
                                            )}
                                            {/* Heatmap Bar */}
                                            {intensity > 0 && (
                                                <Box 
                                                    sx={{
                                                        position: 'absolute',
                                                        bottom: 0,
                                                        left: 0,
                                                        height: 3,
                                                        width: `${intensity * 100}%`,
                                                        background: `linear-gradient(90deg, ${designTokens.colors.primary.main}, ${designTokens.colors.error.main})`,
                                                        opacity: 0.8,
                                                        borderTopRightRadius: 2,
                                                    }} 
                                                />
                                            )}
                                        </Box>
                                    </Fade>
                                </Grid>
                            );
                        })}
                    </Grid>
                </Paper>

                {/* Daily Trend Chart */}
                <Paper
                    sx={{
                        p: 2,
                        mt: 2,
                        borderRadius: designTokens.borderRadius.lg,
                        background: designTokens.colors.background.paper,
                        border: designTokens.glassmorphism.border,
                        boxShadow: designTokens.shadows.md,
                        height: 200,
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    <Typography variant="subtitle2" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ShowChart fontSize="small" color="primary" />
                        <FormattedMessage id="dailyTrend" defaultMessage="Daily Trend" />
                    </Typography>
                    <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={chartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                            <defs>
                                <linearGradient id="colorAmount" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor={designTokens.colors.primary.main} stopOpacity={0.4}/>
                                    <stop offset="95%" stopColor={designTokens.colors.primary.main} stopOpacity={0}/>
                                </linearGradient>
                            </defs>
                            <XAxis
                                dataKey="day"
                                tick={{ fontSize: 10, fill: designTokens.colors.text.secondary }}
                                axisLine={false}
                                tickLine={false}
                                minTickGap={10}
                            />
                            <YAxis 
                                tick={{ fontSize: 10, fill: designTokens.colors.text.secondary }}
                                axisLine={false}
                                tickLine={false}
                                tickFormatter={(val) => `€${val}`}
                            />
                            <RechartsTooltip
                                formatter={(value: number) => [`€ ${value.toFixed(2)}`, '']}
                                contentStyle={{
                                    backgroundColor: 'rgba(30, 41, 59, 0.9)',
                                    backdropFilter: 'blur(12px)',
                                    borderRadius: designTokens.borderRadius.md,
                                    border: `1px solid rgba(255, 255, 255, 0.1)`,
                                    fontSize: '0.8rem',
                                    color: 'white',
                                    boxShadow: designTokens.shadows.lg,
                                }}
                            />
                            <Area
                                type="monotone"
                                dataKey="amount"
                                stroke={designTokens.colors.primary.main}
                                strokeWidth={2}
                                fillOpacity={1}
                                fill="url(#colorAmount)"
                                activeDot={{ r: 6, fill: designTokens.colors.primary.main, stroke: '#fff', strokeWidth: 2 }}
                                onClick={(entry: any) => {
                                    if (entry && entry.activePayload && entry.activePayload[0]) {
                                        handleDateClick(parseISO(entry.activePayload[0].payload.fullDate));
                                    }
                                }}
                            />
                        </AreaChart>
                    </ResponsiveContainer>
                </Paper>
            </Box>

            {/* Details Section */}
            <Box sx={{ flex: { xs: '1 1 auto', md: '1 1 0' }, minWidth: { md: 300 }, display: 'flex', flexDirection: 'column' }}>
                <Paper
                    sx={{
                        p: 3,
                        height: '100%',
                        borderRadius: designTokens.borderRadius.lg,
                        background: designTokens.colors.background.paper,
                        border: designTokens.glassmorphism.border,
                        boxShadow: designTokens.shadows.md,
                        overflow: 'auto',
                    }}
                >
                    <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Event color="primary" />
                        {selectedDate
                            ? format(selectedDate, 'PPP', { locale: dateFnsLocale })
                            : <FormattedMessage id="selectADay" defaultMessage="Select a day" />}
                    </Typography>

                    <Divider sx={{ my: 2 }} />

                    {selectedDate && selectedDayExpenses.length > 0 && (
                        <Box sx={{ mb: 2, p: 2, borderRadius: designTokens.borderRadius.md, bgcolor: designTokens.colors.background.elevated, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Box>
                                <Typography variant="caption" color="text.secondary">Total</Typography>
                                <Typography variant="subtitle1" fontWeight="bold" color="primary.main">
                                    € {getDayTotal(selectedDayExpenses).toFixed(2)}
                                </Typography>
                            </Box>
                            <Box sx={{ textAlign: 'right' }}>
                                <Typography variant="caption" color="text.secondary">Max</Typography>
                                <Typography variant="subtitle2" fontWeight="bold">
                                    € {Math.max(...selectedDayExpenses.map(e => e.amount)).toFixed(2)}
                                </Typography>
                            </Box>
                        </Box>
                    )}

                    {selectedDate && selectedDayExpenses.length === 0 ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 8, opacity: 0.6 }}>
                            <ReceiptLong sx={{ fontSize: 64, color: designTokens.colors.text.disabled, mb: 2 }} />
                            <Typography variant="body1" color="text.secondary" fontWeight="medium">
                                <FormattedMessage id="noExpenses" defaultMessage="No expenses for this day" />
                            </Typography>
                        </Box>
                    ) : (
                        <List>
                            {selectedDayExpenses.map((expense, index) => (
                                <Fade in={true} timeout={300 + index * 100} key={expense.id}>
                                    <Paper
                                        elevation={0}
                                        sx={{
                                            mb: 1.5,
                                            p: 1.5,
                                            borderRadius: designTokens.borderRadius.md,
                                            bgcolor: designTokens.colors.background.default,
                                            border: `1px solid ${designTokens.colors.background.elevated}`,
                                            transition: `all ${designTokens.transitions.fast}`,
                                            '&:hover': {
                                                bgcolor: designTokens.colors.background.elevated,
                                                transform: 'translateY(-1px)',
                                            }
                                        }}
                                    >
                                        <ListItem alignItems="center" sx={{ p: 0 }}>
                                            <ListItemAvatar>
                                                <Avatar
                                                    src={getBrandLogo(expense.description) || getBrandLogo(expense.payer) || undefined}
                                                    alt={expense.description}
                                                    sx={{
                                                        bgcolor: designTokens.colors.background.elevated,
                                                        color: designTokens.colors.text.primary,
                                                        img: { objectFit: 'contain' }
                                                    }}
                                                >
                                                    {!getBrandLogo(expense.description) && !getBrandLogo(expense.payer) && getCategoryIcon(expense.category, categoryIcons)}
                                                </Avatar>
                                            </ListItemAvatar>
                                            <ListItemText
                                                primary={
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                        <Typography variant="subtitle2" fontWeight="bold" sx={{ pr: 1 }}>
                                                            {expense.description}
                                                        </Typography>
                                                        <Typography variant="subtitle2" fontWeight="bold" color="error" sx={{ whiteSpace: 'nowrap' }}>
                                                            € {expense.amount.toFixed(2)}
                                                        </Typography>
                                                    </Box>
                                                }
                                                secondary={
                                                    <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            {!getBrandLogo(expense.description) && !getBrandLogo(expense.payer) ? null : (
                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                    {getCategoryIcon(expense.category, categoryIcons)}
                                                                    <Typography variant="caption" color="text.secondary">
                                                                        {expense.category}
                                                                    </Typography>
                                                                </Box>
                                                            )}
                                                            {(!getBrandLogo(expense.description) && !getBrandLogo(expense.payer)) && (
                                                                <Typography variant="caption" color="text.secondary">
                                                                    {expense.category}
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                            <Chip
                                                                label={expense.payer}
                                                                size="small"
                                                                sx={{
                                                                    height: 20,
                                                                    fontSize: '0.7rem',
                                                                    bgcolor: payerColors[expense.payer],
                                                                    color: '#fff',
                                                                }}
                                                            />
                                                            {expense.shared && (
                                                                <Chip
                                                                    label="Shared"
                                                                    size="small"
                                                                    variant="outlined"
                                                                    sx={{ height: 20, fontSize: '0.7rem' }}
                                                                />
                                                            )}
                                                        </Box>
                                                    </Box>
                                                }
                                            />
                                        </ListItem>
                                    </Paper>
                                </Fade>
                            ))}
                        </List>
                    )}

                </Paper>
            </Box>
        </Box>
    );
};

export default CalendarView;
